const saveBankManualPosting = async (posting) => {
  const entries = await buildManualPostingEntries([posting]);
  const result = await authorizedPost('manualPostingMutationHttp', { businessId: runtime.activeBusinessId, action: 'upsert_from_bank', postings: entries });
  if (result?.state) { runtime.appState = mergeAppMutationState(result.state); emitRuntime(); }
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return safeArray(result?.postings)[0] || entries[0];
};

const manualPostingCorrectionOperationId = () => globalThis.crypto?.randomUUID?.()
  || `manual-correction-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const stableManualPostingCorrectionOperationId = (kind, postingId, payload) => {
  const signature = JSON.stringify(payload); let hash = 2166136261;
  for (let index = 0; index < signature.length; index += 1) hash = Math.imul(hash ^ signature.charCodeAt(index), 16777619);
  return `manual-correction:${kind}:${String(postingId || '').replace(/[^A-Za-z0-9._:-]/g, '-').slice(0, 80)}:${(hash >>> 0).toString(16)}`;
};
const manualPostingRecording = (posting) => posting?.accountingRecordings?.[`manualPosting:${posting?.id}`] || null;
const submitPostedManualPostingCorrection = async (original, replacementPosting, { reason, operationId } = {}) => {
  const recording = manualPostingRecording(original);
  if (!recording?.fingerprint) throw new Error(t('svc.manualCorrectionRecordedOnly', 'Bare bokførte frie posteringer kan korrigeres på denne måten.'));
  const correctionReason = String(reason || '').trim();
  if (correctionReason.length < 3) throw new Error(t('svc.manualCorrectionReasonRequired', 'Skriv hvorfor bilaget korrigeres.'));
  const result = await authorizedPost('manualPostingMutationHttp', {
    businessId: runtime.activeBusinessId, action: 'correct_posted', postingId: original.id,
    operationId: operationId || manualPostingCorrectionOperationId(), expectedFingerprint: recording.fingerprint,
    correctionReason, replacementPosting,
  });
  if (result?.state) { runtime.appState = mergeAppMutationState(result.state); emitRuntime(); }
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return safeArray(result?.postings)[0] || null;
};

const correctPostedManualPosting = async (postingOrId, draft, options = {}) => {
  const postingId = typeof postingOrId === 'string' ? postingOrId : postingOrId?.id;
  const original = safeArray(runtime.appState?.expenses).find((entry) => entry.id === postingId && entry.manualPosting)
    || (postingOrId && typeof postingOrId === 'object' ? postingOrId : null);
  if (!original) throw new Error(t('svc.manualEntryToCancelNotFound'));
  const [replacement] = await buildManualPostingEntries([{ ...draft, id: undefined, voucherNumber: undefined, createdAt: undefined }]);
  return submitPostedManualPostingCorrection(original, replacement, options);
};

const correctManualPostingEntryLocal = async (postingId, patch = {}) => {
  const existing = safeArray(runtime.appState?.expenses).find((expense)=>expense.id === postingId && expense.manualPosting);
  if (!existing) throw new Error(t('svc.manualEntryToCancelNotFound'));
  const nextDate = String(patch.date || existing.date || '').slice(0, 10);
  const bankSideMatch = String(patch.bankPostId || '').match(/-manual-bank-(\d+)-(from|to)$/);
  const lineIndex = bankSideMatch ? Number(bankSideMatch[1]) : safeArray(existing.manualPostingLines).findIndex((line) => /^19[2-9]\d$/.test(String(line.fromAccount || line.toAccount || '')));
  const selectedLine = safeArray(existing.manualPostingLines)[lineIndex];
  const side = bankSideMatch?.[2] || (/^19[2-9]\d$/.test(String(selectedLine?.fromAccount || '')) ? 'from' : 'to');
  const counterSide = side === 'from' ? 'to' : 'from';
  const nextAmount = patch.amount == null || patch.amount === '' ? null : Math.abs(num(patch.amount));
  const nextAccount = String(patch.accountCode || '').trim();
  assertSimpleVatMutationAllowed({previousDate: existing.date, nextDate, vatAffects: true});
  const now = nowIso();
  let nextPosting = null;
  await updateStateAndPersist((state) => ({ ...state, expenses: safeArray(state.expenses).map((expense) => {
    if (expense.id !== postingId || !expense.manualPosting) return expense;
    const lines = safeArray(expense.manualPostingLines).map((line, index) => {
      if (index !== lineIndex) return line;
      const nextLine = { ...line };
      if (nextAmount != null && nextAmount > 0) { nextLine[`${side}Amount`] = nextAmount; nextLine[`${counterSide}Amount`] = nextAmount; }
      if (nextAccount) nextLine[`${counterSide}Account`] = nextAccount;
      if (patch.vatRate != null && patch.vatRate !== '') nextLine[`${counterSide}VatCode`] = manualPostingVatCodeFromRate(patch.vatRate, nextAccount || nextLine[`${counterSide}Account`]);
      return nextLine;
    });
    const receipt = String(patch.receiptInboxEntryId || '').trim() ? safeArray(state.receiptInbox).find((entry) => entry?.id === patch.receiptInboxEntryId) : null;
    nextPosting = stripUndefinedDeep({ ...expense, date: nextDate, manualPostingLines: lines,
      attachmentName: receipt ? (receipt.attachmentName || 'Bilag') : expense.attachmentName,
      receiptInboxEntryId: receipt ? receipt.id : expense.receiptInboxEntryId,
      correctedAt: now, correctionReason: 'Rettet fra bankavstemming.', updatedAt: now });
    return nextPosting;
  }) }));
  window.dispatchEvent(new Event(BACKEND_EVENTS.state));
  return nextPosting;
};
